// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Typography } from '../../lib/base';
import { compareCropsByName } from '../crop/sortCropsByName';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface GanttPlanting {
  readonly planting_time: number; // Unix time
  readonly id: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
    readonly total_grow_time: number; // In seconds
  } | null;
}

interface Props {
  readonly orderTime: number; // Unix time
  readonly plantings: readonly GanttPlanting[];
}

export default function OrderGanttChart({ orderTime, plantings: plantingsIn }: Props) {
  const { timezone } = useUserContext();
  const theme = useTheme();

  if (plantingsIn.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
        }}
      >
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          No plantings found
        </Typography>
      </Box>
    );
  }

  // TODO: should use color mode theme utility
  const textColor = theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[900];
  const tickColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400];

  // If surplus is assigned to an order, there could be multiple plantings for the same crop
  // In almost all situations they have the same timeline parameters
  // But if one planting is different somehow. Like grow time changed during the order, for example,
  // then we'd want to show both plantings with different timelines
  const endDate = dayjs.unix(orderTime).tz(timezone);

  const uniquePlantingKeys = new Map<string, number>();
  const getPlantingKey = (p: GanttPlanting) =>
    `${p.planting_time}-${p.crop?.id}-${p.planting_time - (p.crop?.total_grow_time ?? 0)}`;
  const plantings = plantingsIn
    .filter((p) => {
      const key = getPlantingKey(p);
      const currentCount = uniquePlantingKeys.get(key) ?? 0;
      uniquePlantingKeys.set(key, currentCount + 1);

      return currentCount === 0;
    })
    .map((p) => ({
      id: p.id,
      plantingTime: p.planting_time,
      name: p.crop?.crop_name ?? 'Crop Not Found',
      growTime: p.crop?.total_grow_time ?? 0,
      startDate: dayjs.unix(p.planting_time - (p.crop?.total_grow_time ?? 0)).tz(timezone),
      endDate: dayjs.unix(p.planting_time).tz(timezone),
    }));
  // Fail safe, in case any plantings are after the end date/order time (which shoudln't ever happen)
  // Leaving out for now because it can actually provide a strong visual indication to the user
  // that something is very wrong
  // .filter((p) => p.endDate.isBefore(endDate) || p.endDate.isSame(endDate))
  const earliestStartDate = plantings.reduce(
    (acc, p) => (p.startDate.isBefore(acc) ? p.startDate : acc),
    plantings[0]?.startDate ?? dayjs(),
  );

  const totalDays = endDate.diff(earliestStartDate, 'day');
  const timelineTicks = Array.from({ length: totalDays + 1 }, (_, i) => earliestStartDate.add(i, 'day'));
  const timesToDisplay = new Set([
    ...plantings.map((p) => p.startDate.unix()),
    ...plantings.map((p) => p.endDate.unix()),
    endDate.unix(),
  ]);

  const sortedPlantings = [...plantings];
  sortedPlantings.sort((a, b) => compareCropsByName(a.name, b.name));

  const today = dayjs().tz(timezone);
  const isToday = (date: dayjs.Dayjs) => today.format('YYYY-MM-DD') === date.format('YYYY-MM-DD');

  return (
    <Box
      sx={{
        width: 'calc(100% - 40px)', // Account for padding
        height: `calc(${plantings.length * 30 + 50}px + 40px)`, // Account for padding
        position: 'relative',
        overflow: 'visible', // Allow overflow for date labels
        margin: '20px', // Simulate padding with margin
      }}
    >
      {/* Timeline */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '0',
          right: '0',
          height: '30px',
        }}
      >
        {/* Timeline and Date Labels */}
        <Box sx={{ position: 'absolute', bottom: '20px', left: '20px', right: '20px', height: '50px' }}>
          {timelineTicks.map((tick, index) => {
            const isFirst = index === 0;
            const isLast = index === totalDays;
            return (
              <Box
                key={tick.unix()}
                sx={{
                  position: 'absolute',
                  left: isFirst ? '0%' : isLast ? '100%' : `${(index / totalDays) * 100}%`,
                  transform: isFirst ? 'translateX(0%)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                  bottom: '0',
                }}
              >
                <Box sx={{ mb: '15px', height: '10px', width: '2px', backgroundColor: tickColor }}>
                  <Box
                    sx={{
                      color: textColor,
                      position: 'absolute',
                      top: '10px',
                      width: '100px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  />
                  {(timesToDisplay.has(tick.unix()) || isToday(tick)) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: textColor,
                        position: 'absolute',
                        top: '10px',
                        width: '100px',
                        left: '50%',
                        transform: 'translateX(-25%) rotate(-25deg)',
                      }}
                    >
                      {isToday(tick) ? 'Today' : tick.format('MMM D')}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* Plantings */}
      {sortedPlantings.map((planting, index) => {
        const startDiff = planting.startDate.diff(earliestStartDate, 'day');
        const endDiff = planting.endDate.diff(earliestStartDate, 'day');
        const widthPercent = ((endDiff - startDiff) / totalDays) * 100;

        return (
          <Box
            key={planting.id}
            sx={{
              position: 'absolute',
              top: `calc(${index * 30}px + 20px)`,
              left: `calc(${(startDiff / totalDays) * 100}% + 20px)`,
              width: `calc(${widthPercent}% - 40px)`, // Adjust for padding
              height: '20px',
              backgroundColor: 'secondary.main',
              borderRadius: '10px',
              color: 'white',
              textAlign: 'center',
              lineHeight: '20px',
              overflow: 'hidden',
            }}
          >
            <Typography variant="body2">{planting.name}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
